import React, {useEffect, useState} from 'react';
import block from 'bem-cn-lite';
import './QuantityInput.scss';
import {Button, TextInput, Text} from '@gravity-ui/uikit';
import {Minus, Plus} from '@gravity-ui/icons';

const b = block('quantity-input');

export interface QuantityInputProps {
    className?: string;
    value: number;
    multiplicity: number; // количество в корзине/заказе должно быть кратно этому параметру
    onChange: (newValue: number) => void;
    disabled?: boolean; // Новый пропс для отключения кнопок
    errorText?: string; // Новый пропс для показа красного текста
    maxQuantity: number; // Новый пропс для максимального количества itemsCnt
    onDecrease?: () => void; // Для поиска, чтобы делать активным минус и удалить по клику
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
    className,
    value,
    multiplicity,
    onChange,
    disabled,
    errorText,
    maxQuantity,
    onDecrease
}) => {
    const [inputValue, setInputValue] = useState<string>(value.toString());

    const errorTextClass =
        errorText === 'Товар закончился' ? b('error-text-end') : b('error-text-start');

    useEffect(() => {
        setInputValue(value.toString());
    }, [value]);

    const handleIncrease = () => {
        if (value + multiplicity <= maxQuantity) {
            const newQuantity = value + multiplicity;
            setInputValue(newQuantity.toString());
            onChange(newQuantity);
        }
    };

    const handleDecrease = () => {
        if (value > multiplicity) {
            const newQuantity = value - multiplicity;
            setInputValue(newQuantity.toString());
            onChange(newQuantity);
        } else if (value === multiplicity && onDecrease) {
            // Если количество multiplicity и передана функция onDecrease, вызываем её
            onDecrease();
        }
    };

    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;

        if (input === '') {
            // Разрешаем очистить поле, но не трогаем внешнее value
            setInputValue('');
            return;
        }

        const newValue = parseInt(input, 10);

        if (isNaN(newValue) || newValue <= 0) {
            // Некорректный ввод — просто обновляем текст, но не вызываем onChange
            setInputValue(input);
            return;
        }

        if (newValue > maxQuantity) {
            setInputValue(maxQuantity.toString());
            if (maxQuantity % multiplicity === 0) {
                onChange(maxQuantity);
            }
            return;
        }

        if (multiplicity <= 1) {
            setInputValue(input);
            onChange(newValue);
            return;
        }

        // multiplicity > 1 — разрешаем только кратные значения
        setInputValue(input);

        if (newValue % multiplicity === 0) {
            onChange(newValue);
        }
    };

    const handleBlur = () => {
        // Если поле оставлено пустым, возвращаем исходное значение
        if (inputValue === '') {
            setInputValue(value.toString());
            return;
        }

        const newValue = parseInt(inputValue, 10);

        if (isNaN(newValue) || newValue <= 0) {
            setInputValue(value.toString());
            return;
        }

        let corrected = newValue;

        if (corrected > maxQuantity) {
            corrected = maxQuantity;
        }

        if (multiplicity > 1) {
            // Приводим к ближайшему "вниз" кратному multiplicity
            corrected = Math.floor(corrected / multiplicity) * multiplicity;

            // Не даём опуститься ниже одного шага, если пользователь вводил > 0
            if (corrected === 0) {
                corrected = multiplicity;
            }
        }

        setInputValue(corrected.toString());

        if (corrected !== value) {
            onChange(corrected);
        }
    };

    const isDecreaseDisabled = disabled || value <= 0 || (value === multiplicity && !onDecrease);

    return (
        <div className={b(null, className)}>
            <div className={b('container')}>
                <Button
                    disabled={isDecreaseDisabled}
                    size="m"
                    className={b('button')}
                    onClick={handleDecrease}
                >
                    <Minus />
                </Button>

                <TextInput
                    size="s"
                    view="clear"
                    className={b('input')}
                    value={inputValue}
                    onChange={handleChangeInput}
                    onBlur={handleBlur}
                    onKeyDown={(event) => {
                        if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            handleIncrease();
                        } else if (event.key === 'ArrowDown') {
                            event.preventDefault();
                            handleDecrease();
                        }
                    }}
                ></TextInput>

                <Button
                    size="m"
                    className={b('button')}
                    onClick={handleIncrease}
                    disabled={disabled || value + multiplicity > maxQuantity}
                >
                    <Plus />
                </Button>

                {errorText && (
                    <Text className={errorTextClass} variant="caption-1" color="danger">
                        {errorText}
                    </Text>
                )}
            </div>
        </div>
    );
};
